import moment from 'moment/moment';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import BookingCalendar from './BookingCalendar';

const SlotSelection = ({
    timeSlot, setTimeSlot, slotsdata, InstructorCourse, blockedDatesByInstructor = {},
    sessionsRequired = 1, selected_sessions = [], setSelectedSessions,
    candidateInstructorIds, setCandidateInstructorIds, carType,
}) => {
    const isMultiSession = sessionsRequired > 1;
    const dailySlotDuration = 2;

    const allCourseInstructorIds = useMemo(() => {
        const ids = new Set();
        slotsdata.forEach((s) => ids.add(s.instructorid));
        return Array.from(ids);
    }, [slotsdata]);

    const calculateEndDate = (startDate, totalHours) => {
        const daysRequired = Math.ceil(totalHours / dailySlotDuration);
        let weekdaysCount = 0;
        let currentDate = moment(startDate);
        while (weekdaysCount < daysRequired) {
            if (currentDate.isoWeekday() < 6) weekdaysCount++;
            if (weekdaysCount < daysRequired) currentDate.add(1, 'days');
        }
        return currentDate.toDate();
    };

    const isInstructorOnLeave = (instructorId, selectedDate) => {
        const ranges = blockedDatesByInstructor[instructorId] || [];
        const day = moment(selectedDate).format('YYYY-MM-DD');
        return ranges.some((r) => day >= r.start_date && day <= r.end_date);
    };

    const isSlotBlockedForInstructor = (instructorId, selectedDate, slotTime) => {
        const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD');
        const [slotStartTime, slotEndTime] = slotTime.split(' - ');
        const slotStart = moment(selectedDateStr + ' ' + slotStartTime, 'YYYY-MM-DD hh:mm A');
        const slotEnd = moment(selectedDateStr + ' ' + slotEndTime, 'YYYY-MM-DD hh:mm A');

        return InstructorCourse.some((lesson) => {
            if (lesson._instructorId !== instructorId) return false;
            const lessonStartDate = moment(lesson.bookingdate).format('YYYY-MM-DD');
            const totalHours = Number(lesson?.cources?.theory) + Number(lesson?.cources?.practical);
            const lessonEndDate = moment(calculateEndDate(lessonStartDate, totalHours)).format('YYYY-MM-DD');
            const [lessonStartTime, lessonEndTime] = lesson.bookingtime.split(' - ');

            let currentLessonDate = moment(lessonStartDate);
            while (currentLessonDate.isSameOrBefore(lessonEndDate)) {
                const currentDaySlotStart = moment(currentLessonDate.format('YYYY-MM-DD') + ' ' + lessonStartTime, 'YYYY-MM-DD hh:mm A');
                const currentDaySlotEnd = moment(currentLessonDate.format('YYYY-MM-DD') + ' ' + lessonEndTime, 'YYYY-MM-DD hh:mm A');
                const isDateOverlap = selectedDateStr === currentLessonDate.format('YYYY-MM-DD');
                const isTimeOverlap = slotStart.isBefore(currentDaySlotEnd) && slotEnd.isAfter(currentDaySlotStart);
                if (isDateOverlap && isTimeOverlap) return true;
                currentLessonDate.add(1, 'days');
            }
            return false;
        });
    };

    // Merge every candidate instructor's window for a given day into one set of
    // time chips, each remembering which instructor(s) actually offer it so the
    // Instructor step can be narrowed to real matches instead of the whole course roster.
    const getAvailableSlots = (CurrentDate) => {
        const dayName = moment(CurrentDate).format("dddd");
        const dayEntries = slotsdata.filter((slot) => slot.days === dayName);
        const windowMap = new Map();

        dayEntries.forEach((entry) => {
            const instructorId = entry.instructorid;
            if (candidateInstructorIds && !candidateInstructorIds.includes(instructorId)) return;
            if (isInstructorOnLeave(instructorId, CurrentDate)) return; // instructor blocked this day
            const parsedSlots = entry?.slots ? JSON.parse(entry.slots) : [];
            parsedSlots.forEach((parsedSlot) => {
                const slotTime = `${parsedSlot.start} - ${parsedSlot.end}`;
                if (isSlotBlockedForInstructor(instructorId, CurrentDate, slotTime)) return;
                const key = `${parsedSlot.start}|${parsedSlot.end}|${parsedSlot.location || ''}|${parsedSlot.lesson_type || ''}`;
                if (!windowMap.has(key)) {
                    windowMap.set(key, { ...parsedSlot, instructorIds: [] });
                }
                windowMap.get(key).instructorIds.push(instructorId);
            });
        });

        return Array.from(windowMap.values()).sort((a, b) => a.start.localeCompare(b.start));
    };

    const [selectedDateStr, setSelectedDateStr] = useState(
        () => timeSlot?.date || moment().format('YYYY-MM-DD')
    );
    const [calendarOpen, setCalendarOpen] = useState(false);
    const dateValue = useMemo(() => moment(selectedDateStr, 'YYYY-MM-DD').toDate(), [selectedDateStr]);

    // Generate a 14-day quick-pick strip starting today - unless the calendar
    // popup was used to jump past it, in which case the strip re-centers on
    // that date so it's actually visible (and highlighted) in the row.
    const dateCards = useMemo(() => {
        const today = moment().startOf('day');
        const selected = moment(selectedDateStr, 'YYYY-MM-DD');
        const windowStart = selected.isAfter(today.clone().add(13, 'days'), 'day')
            ? selected.clone()
            : today.clone();

        const cards = [];
        for (let i = 0; i < 14; i++) {
            const d = windowStart.clone().add(i, 'days').toDate();
            cards.push({
                date: d,
                dateStr: moment(d).format('YYYY-MM-DD'),
                weekday: moment(d).format('ddd'),
                dayNum: moment(d).format('D'),
                month: moment(d).format('MMM'),
                monthYear: moment(d).format('MMMM YYYY'),
            });
        }
        return cards;
    }, [selectedDateStr]);

    const dateStripRef = useRef(null);

    const scrollDateStrip = (direction) => {
        dateStripRef.current?.scrollBy({ left: direction * 220, behavior: 'smooth' });
    };

    // The strip's scrollbar is hidden for a cleaner look, so give mouse users
    // (who have no touch-swipe gesture) a way to scroll it: a normal vertical
    // wheel motion pans the row horizontally, same as the chevron buttons.
    const handleDateStripWheel = (e) => {
        if (!dateStripRef.current || e.deltaY === 0) return;
        e.preventDefault();
        dateStripRef.current.scrollLeft += e.deltaY;
    };

    const currentMonthYear = moment(selectedDateStr, 'YYYY-MM-DD').format('MMMM YYYY');

    const availableSlots = useMemo(() => {
        return getAvailableSlots(dateValue);
    }, [dateValue, slotsdata, InstructorCourse, candidateInstructorIds, blockedDatesByInstructor]);

    const selectDate = (dateStr) => {
        setSelectedDateStr(dateStr);
        setTimeSlot((prev) => ({
            ...prev,
            date: dateStr,
            time: ''
        }));
        // No time is committed for this date yet, so drop any narrowing from a
        // previously-picked (now-abandoned) date - single-session courses only.
        if (!isMultiSession) setCandidateInstructorIds(null);
    };

    useEffect(() => {
        // Only auto-pick a date when nothing has been chosen yet - otherwise this
        // fires again every time this step remounts (e.g. clicking Back from
        // Instructor) and silently overwrites a date/time the customer already set.
        const alreadyHasSelection = isMultiSession ? selected_sessions.length > 0 : !!timeSlot?.time;
        if (alreadyHasSelection) return;
        if (slotsdata && slotsdata.length > 0) {
            for (let i = 0; i < dateCards.length; i++) {
                const slots = getAvailableSlots(dateCards[i].date);
                if (slots.length > 0) {
                    setSelectedDateStr(dateCards[i].dateStr);
                    setTimeSlot((prev) => ({
                        ...prev,
                        date: dateCards[i].dateStr,
                        time: prev.time || ''
                    }));
                    return;
                }
            }
        }
    }, [slotsdata]);

    const onDateCardClick = (card) => selectDate(card.dateStr);

    const onCalendarDaySelect = (day) => {
        selectDate(day.format('YYYY-MM-DD'));
        setCalendarOpen(false);
    };

    const handleTimeSelect = (slot) => {
        const curr_slot = `${moment(slot.start, "HH:mm").format("hh:mm A")} - ${moment(slot.end, "HH:mm").format("hh:mm A")}`;
        const sessionInfo = { time: curr_slot, location: slot.location, lesson_type: slot.lesson_type, instructorIds: slot.instructorIds };

        const narrowed = candidateInstructorIds
            ? candidateInstructorIds.filter((id) => slot.instructorIds.includes(id))
            : slot.instructorIds;
        setCandidateInstructorIds(narrowed);

        if (isMultiSession) {
            if (selected_sessions.some((s) => s.date === selectedDateStr)) {
                toast.error("You've already picked a lesson on this date.");
                return;
            }
            setSelectedSessions((prev) => [...prev, { date: selectedDateStr, ...sessionInfo }]);
        } else {
            setTimeSlot((prev) => ({ ...prev, ...sessionInfo }));
        }
    };

    const handleRemoveSession = (index) => {
        setSelectedSessions((prev) => {
            const next = prev.filter((_, i) => i !== index);
            const recalculated = next.reduce(
                (acc, s) => acc.filter((id) => (s.instructorIds || allCourseInstructorIds).includes(id)),
                allCourseInstructorIds
            );
            setCandidateInstructorIds(next.length > 0 ? recalculated : null);
            return next;
        });
    };

    const activeTimeForHighlight = isMultiSession
        ? selected_sessions.find((s) => s.date === selectedDateStr)?.time
        : timeSlot?.time;
    const reachedSessionCap = isMultiSession && selected_sessions.length >= sessionsRequired;

    return (
        <div className="booking-slot-wrapper">
            <div className="booking-slot-content">
                <div className="booking-slot-header-row">
                    <div>
                        <h2 className="booking-slot-title">Choose Date & Time</h2>
                        <p className="booking-slot-subtitle">
                            {isMultiSession
                                ? `This course has ${sessionsRequired} lesson sessions. Pick your dates below.`
                                : 'Pick a day and time that works for you.'}
                        </p>
                    </div>
                </div>

                {isMultiSession && selected_sessions.length > 0 && (
                    <div className="booking-sessions-progress">
                        <div className="booking-sessions-progress-label">
                            {selected_sessions.length} of {sessionsRequired} lesson dates selected
                        </div>
                        <div className="booking-sessions-list">
                            {selected_sessions.map((session, index) => (
                                <div key={`${session.date}-${session.time}`} className="booking-session-chip">
                                    <span className="booking-session-chip-index">{index + 1}</span>
                                    <span>{moment(session.date).format('ddd, D MMM')} &middot; {session.time.split(' - ')[0]}</span>
                                    <button type="button" onClick={() => handleRemoveSession(index)} aria-label="Remove">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {reachedSessionCap ? (
                    <div className="booking-sessions-complete">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        All {sessionsRequired} lesson dates are set.
                    </div>
                ) : (
                    <>
                        {/* Month Label - opens the full calendar */}
                        <button
                            type="button"
                            className="booking-date-section-label booking-date-section-label-btn"
                            onClick={() => setCalendarOpen(true)}
                        >
                            <i className="bi bi-calendar3 me-2"></i>
                            {currentMonthYear}
                            <i className="bi bi-chevron-down booking-date-section-caret"></i>
                        </button>

                        {/* Date Cards Horizontal Scroll */}
                        <div className="booking-date-cards-row">
                            <button
                                type="button"
                                className="booking-date-cards-nav"
                                onClick={() => scrollDateStrip(-1)}
                                aria-label="Scroll to earlier dates"
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <div
                                className="booking-date-cards-scroll"
                                ref={dateStripRef}
                                onWheel={handleDateStripWheel}
                            >
                                {dateCards.map((card) => (
                                    <div
                                        key={card.dateStr}
                                        className={`booking-date-card${card.dateStr === selectedDateStr ? ' selected' : ''}`}
                                        onClick={() => onDateCardClick(card)}
                                    >
                                        <span className="booking-date-card-weekday">{card.weekday}</span>
                                        <span className="booking-date-card-day">{card.dayNum}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="booking-date-cards-nav"
                                onClick={() => scrollDateStrip(1)}
                                aria-label="Scroll to later dates"
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>

                        {calendarOpen && (
                            <BookingCalendar
                                selectedDateStr={selectedDateStr}
                                onSelectDay={onCalendarDaySelect}
                                onClose={() => setCalendarOpen(false)}
                            />
                        )}

                        {/* Available Times */}
                        <div className="booking-times-section">
                            <h4 className="booking-times-label">
                                <i className="bi bi-clock me-2"></i>
                                Available Times
                            </h4>
                            {availableSlots.length === 0 ? (
                                <div className="booking-times-empty">
                                    <i className="bi bi-calendar-x me-2"></i>
                                    No available time slots for this date. Try another day.
                                </div>
                            ) : (
                                <div className="booking-times-grid">
                                    {availableSlots.map((slot, index) => {
                                        const curr_slot = `${moment(slot.start, "HH:mm").format("hh:mm A")} - ${moment(slot.end, "HH:mm").format("hh:mm A")}`;
                                        const isSelected = curr_slot === activeTimeForHighlight;
                                        return (
                                            <div
                                                key={index}
                                                className={`booking-time-chip${isSelected ? ' selected' : ''}`}
                                                onClick={() => handleTimeSelect(slot)}
                                            >
                                                <div className="booking-time-chip-main">
                                                    <i className="bi bi-clock-fill"></i>
                                                    {moment(slot.start, "HH:mm").format("hh:mm A")}
                                                </div>
                                                <div className="booking-time-chip-meta">
                                                    {slot.location && (
                                                        <span><i className="bi bi-geo-alt-fill me-1"></i>{slot.location}</span>
                                                    )}
                                                    {(slot.lesson_type || carType) && (
                                                        <span><i className="bi bi-car-front-fill me-1"></i>{slot.lesson_type || carType}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SlotSelection;
